import { Fragment, FunctionComponent, useEffect, useState } from 'react';
import Button from '../../forms/Button';
import Pagination from '../../shared/Pagination';
import { Table, TableHead, TableHeader, TableRow } from '../../shared/Table';
import { usePagination } from '../../shared/usePagination';
import ApartmentModal from '../modals/ApartmentModal';
import CreateApartmentModal from '../modals/CreateApartmentModal';
import { ApartmentResponse, LocationResponse } from '../models/GetApartmentsResponse';

type Props = {
    location: LocationResponse;
    refreshApartments: () => void;
    setLocation: (location: LocationResponse | undefined) => void;
};

const ApartmentList: FunctionComponent<Props> = ({ location, refreshApartments, setLocation }) => {
    const [apartment, setApartment] = useState<ApartmentResponse | undefined>(undefined);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const { currentPage, setCurrentPage, currentItems, totalPages, itemsPerPage } = usePagination(location.apartments ?? []);

    useEffect(() => {
        if (location && apartment) {
            const refreshedApartment = location.apartments.find((a) => a.apartmentId === apartment.apartmentId);
            setApartment(refreshedApartment);
        }
    }, [location]);

    return (
        <div className="mb-5">
            <h2>Lägenheter på {location.name}</h2>
            {location.apartments ? (
                <Fragment>
                    <Table withPagination={totalPages > 1} style={{ tableLayout: 'fixed' }}>
                        <TableHead>
                            <tr>
                                <TableHeader>Lägenhetsnr</TableHeader>
                                <TableHeader className="text-center">Yta</TableHeader>
                                <TableHeader className="text-center">Antal rum</TableHeader>
                                <TableHeader className="text-end">Antal boende</TableHeader>
                            </tr>
                        </TableHead>
                        <tbody>
                            {currentItems.map((apartment) => (
                                <TableRow key={apartment.apartmentId} onClick={() => setApartment(apartment)}>
                                    <td>{apartment.name}</td>
                                    <td className="text-center">{formatNumber(apartment.area)}</td>
                                    <td className="text-center">{formatNumber(apartment.numberOfRooms)}</td>
                                    <td className="text-end">{formatNumber(apartment.count)}</td>
                                </TableRow>
                            ))}
                        </tbody>
                    </Table>
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        totalItems={location.apartments.length}
                        itemsPerPage={itemsPerPage}
                        onPageChange={setCurrentPage}
                        ariaLabel="Lägenhetslistans sidnavigering"
                    />
                </Fragment>
            ) : (
                <p>Inga lägenheter på vald adress.</p>
            )}
            <div className="paper__buttons">
                <Button text="Tillbaka" isPrimary={false} additionalClassName="btn-secondary btn-lg btn-prev" onClick={() => setLocation(undefined)} />
                <Button text="Lägg till lägenhet" isPrimary={true} additionalClassName="btn-primary btn-lg btn-plus" onClick={() => setShowCreateModal(true)} />
            </div>
            {apartment && <ApartmentModal apartment={apartment} onClose={() => setApartment(undefined)} refreshApartments={refreshApartments} />}
            {showCreateModal && <CreateApartmentModal location={location} onClose={() => setShowCreateModal(false)} refreshApartments={refreshApartments} />}
        </div>
    );
};

function formatNumber(number: number) {
    return number === 0 ? '-' : number;
}

export default ApartmentList;
